import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { map } from 'rxjs';
import { AuthService } from '../../auth/services/auth.service';
import { Roles } from '../models/roles';
import { TenantOptions } from '../models/tenant-options';

/**
 * Role guard that checks if user has all required roles and tenant options to access a route.
 *
 * Usage in routes:
 * ```
 * {
 *   path: 'admin',
 *   component: AdminComponent,
 *   canActivate: [roleGuard],
 *   data: { requiredRoles: ['ADMIN', 'MANAGER'], requiredTenantOptions: ['SOME_FEATURE'] }
 * }
 * ```
 *
 * If requiredRoles is not present or empty, role check is skipped.
 * If requiredRoles is present, user must have ALL (or ANY, per requiredRolesMode) specified roles.
 * If requiredTenantOptions is present, user must have ALL specified tenant options.
 */
export const roleGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const requiredRoles: string[] = route.data?.['requiredRoles'];
  const requiredRolesMode: string = route.data?.['requiredRolesMode'] || 'all';
  const requiredTenantOptions: string[] = route.data?.['requiredTenantOptions'];

  const checksRoles = Boolean(requiredRoles && requiredRoles.length > 0);
  const checksTenantOptions = Boolean(requiredTenantOptions && requiredTenantOptions.length > 0);

  if (!checksRoles && !checksTenantOptions) {
    return true;
  }

  return authService.ensureUserLoaded().pipe(
    map((loaded) => {
      if (!loaded) {
        return false;
      }

      if (checksRoles) {
        if (requiredRolesMode === 'all') {
          const hasAllRequiredRoles = requiredRoles.every((requiredRole) => authService.hasRole(requiredRole as Roles));

          if (!hasAllRequiredRoles) {
            router.navigate(['/nao-autorizado'], { queryParams: { from: state.url, reason: 'missing_role' } });
            return false;
          }
        }

        const hasAnyRequiredRoles = requiredRoles.some((requiredRole) => authService.hasRole(requiredRole as Roles));

        if (!hasAnyRequiredRoles) {
          router.navigate(['/nao-autorizado'], { queryParams: { from: state.url, reason: 'missing_role' } });
          return false;
        }
      }

      if (checksTenantOptions) {
        const hasAllRequiredTenantOptions = requiredTenantOptions.every((requiredTenantOption) =>
          authService.hasTenantOption(requiredTenantOption as TenantOptions),
        );

        if (!hasAllRequiredTenantOptions) {
          router.navigate(['/nao-autorizado'], { queryParams: { from: state.url, reason: 'missing_tenant_option' } });
          return false;
        }
      }

      return true;
    }),
  );
};
